/**
 * Player intent, as data.
 *
 * Nothing outside the simulation ever mutates world state directly — the UI, the
 * debug console and the scripted bots all speak this one vocabulary. That is
 * what makes replays, the headless harness and the determinism test possible:
 * seed + command log fully determines the run.
 */

export const Cmd = {
  /** Supporting heroes: class in s; casts carry x/y in a/b and hero id in c. */
  RecruitHero: 224,
  CompanionAbility: 225,
  ReviveCompanion: 226,
  /** Campaign army id in a, class in s. */
  CampaignRecruitHero: 227,
  Build: 0,
  Demolish: 1,
  WallLine: 2,
  StartWave: 3,
  MoveCompany: 4,
  SetFormation: 5,
  SetStance: 6,
  CastPower: 7,
  Recruit: 8,
  Reinforce: 9,
  PickRelic: 10,
  Research: 11,
  UpgradeBuilding: 12,
  UpgradePower: 13,
  DisbandCompany: 14,
  ReviveHero: 15,
  /** Total War-style drag order: set the company's facing and frontage. */
  SetFacing: 19,
  RoadLine: 16,
  /** Put back whatever the rubble under this tile used to be. */
  RebuildRubble: 17,
  ClearRubble: 18,
  /** Move the tax dial. `a` is an index into TAX_BANDS. */
  SetTaxPolicy: 20,
  /** Spend a hero skill point. `s` is the skill id. */
  HeroSkill: 21,
  /** Cast a hero ability at a point. `s` is the ability id. */
  HeroAbility: 22,
  /** Answer a chronicle event. `s` is the event id, `a` the choice index. */
  ResolveEvent: 23,
  /** Upgrade a stretch of wall to the next tier. */
  UpgradeWallLine: 24,
  /** Dig a ditch along a drag-line. */
  MoatLine: 25,
  /** Buy or sell at the market. `s` is the resource key, `a` the signed amount. */
  Trade: 26,

  /**
   * Choose the character this run is about. `s` is a hero class id.
   *
   * This was authored as 40, to sit clear of the settlement vocabulary — and
   * the world map, built in parallel, opened its own block at 40 for exactly
   * the same reason. Two agents avoiding a collision chose the same gap. It
   * belongs here anyway: `ReviveHero` (15), `HeroSkill` (21) and `HeroAbility`
   * (22) were already in this range, so 40 was the odd one out.
   */
  PickHero: 27,

  /**
   * Begin researching the next Age. No argument — there is only ever one next.
   *
   * 28 is taken deliberately and is declared in COORDINATION.md, per the rule
   * three parallel branches learned the hard way: leaving a gap is not a
   * strategy, because everyone leaves the same gap.
   */
  AdvanceAge: 28,

  /**
   * Buy the next rank for a unit type. `s` is the unit id.
   *
   * 29, declared in COORDINATION.md before it was taken.
   */
  UpgradeUnitRank: 29,

  /**
   * Jump the Long Ledger to a given day. `a` is the absolute day since the run
   * began; `src/sim/systems/calendar.ts` turns that into a month and a season.
   *
   * 30 and 31 are the calendar's, declared in COORDINATION.md before either was
   * taken. 31 is deliberately still free: the season system needs no player
   * command — the decision winter asks for is "did you fill the granary in
   * Tally", and that is made with the buildings that already exist.
   *
   * A debug command rather than a player one, and it goes through the same door
   * as everything else, so a session that used it is still a legal replay.
   */
  DebugSetDate: 30,

  /**
   * Hold this world because the player is away fighting a battle. `a` is 1 to
   * hold and 0 to release.
   *
   * 34–36 were reserved in COORDINATION.md for the real-time pass. Only 34 is
   * taken: **35 and 36 are released unused.** Speed and pause turned out not to
   * need commands at all — the simulation runs at a fixed 30 Hz and always will,
   * so "faster" means the frame loop feeding it more ticks per second and
   * "paused" means feeding it none. Neither changes a world, so neither belongs
   * in the command log; a replay of a session played at ×8 is byte-identical to
   * one played at ×1, which is the property that makes speed controls safe.
   *
   * A hold is different, and is the one thing here that *is* a world's business:
   * see `World.battleHold`.
   */
  SetBattleHold: 34,
  /**
   * Hand the realm a capital's measure. `a` is development × 1000, `b` the
   * fortification, `c` the province it belongs to.
   *
   * This used to be a direct write, made by the renderer at the one moment
   * both worlds were in hand — walking out to the map. That is fine while one
   * machine owns the run and wrong the moment two do: one player opening the
   * map changed a world the other was also simulating, and the room went out
   * of step with nothing in the log to say why. It is a command now, so every
   * client makes the same write on the same tick.
   */
  SetCapitalStanding: 35,
  /**
   * Kill that one. `a` is the ordering company, `b` the company to break,
   * or −1 to let them choose their own targets again.
   */
  AttackCompany: 36,

  // ── The campaign world map ───────────────────────────────────────────────
  // A contiguous block starting at 40, deliberately clear of the settlement
  // vocabulary above so the two can grow without colliding. `applyCommand`
  // routes the whole range to `src/sim/campaign/commands.ts`.
  /**
   * Resolve a month at once: march, fight, besiege, collect, let the council sit.
   *
   * No longer a button, and no longer the engine of anything — the realm runs on
   * the Long Ledger and sits a council every `MONTH_SECONDS` whether or not
   * anybody is watching. This survives as the fast-forward for the balance
   * harness and the tests, which are not going to sit through nine hours of
   * simulated realm to find out what a hundred months does. Resolving early does
   * not gain a month: the realm waits for the ledger to catch up.
   */
  CampaignEndTurn: 40,
  /** Muster a host. `a` is the province, `b` the strength points to spend. */
  CampaignRaiseArmy: 41,
  /** March. `a` is the army id, `b` the province to march to. */
  CampaignMoveArmy: 42,
  /** Fold one host into another standing on the same ground. `a` takes `b`. */
  CampaignMergeArmies: 43,
  /** Stand a host down; half of them go home rather than into the ground. */
  CampaignDisbandArmy: 44,
  /** Sit down in front of the walls and wait. `a` is the army id. */
  CampaignBesiege: 45,
  /** Storm the walls at the next resolution rather than waiting. */
  CampaignAssault: 46,
  /**
   * Hand a fought battle's result back to the map. `a` is 1 if the player won
   * and -1 if not; `b` and `c` are each side's survivors in permille.
   * `d = 1` means the actual settlement damage has already been retained.
   */
  CampaignResolveBattle: 47,
  /** Settle the pending battle abstractly, from strengths and the seeded RNG. */
  CampaignAutoResolve: 48,
  /** Raise a held province's walls by one level. `a` is the province. */
  CampaignFortify: 49,
  /** Top a host back up where it stands. `a` is the army id, `b` the points. */
  CampaignReinforce: 50,

  /**
   * 51–58 are the depth pass on the strategic layer — marches that take time and
   * can be caught, warnings, and bandit camps. Reserved in COORDINATION.md
   * before they were taken, which is the rule three parallel branches learned
   * the expensive way: leaving a gap is not a strategy, because everyone leaves
   * the same gap. 54–58 are still free, and are still this block's.
   */
  /** Run a marching host down on the road. `a` is the chaser, `b` the quarry. */
  CampaignIntercept: 51,
  /** Turn a march around. It walks back the way it came. `a` is the army id. */
  CampaignHalt: 52,
  /** Post a bounty on a bandit camp on your own ground. `a` is the province. */
  CampaignBounty: 53,

  /**
   * The cart — items, and what the character does with them.
   *
   * 71–76 are this block's, reserved in COORDINATION.md before they were taken.
   * 70 is deliberately skipped: it sits directly against the campaign's 51–58
   * run and the debug block that starts at 64, and a single spare integer
   * between two neighbours is worth more than the tidiness of starting on a
   * round number.
   */
  /** Wear an item. `a` is its uid; the slot comes from the catalogue. */
  EquipItem: 71,
  /** Take a slot's item off. `a` is an `ItemSlot`. */
  UnequipItem: 72,
  /** Pay to raise an item one level. `a` is its uid. */
  ReforgeItem: 73,
  /** Melt an item down for Souls. `a` is its uid. */
  ScrapItem: 74,
  /**
   * Put an item on the cart. `s` is the catalogue id, `a` the level.
   *
   * A debug command that is numbered with the player's rather than into the
   * 64+ block, because it obeys the same two gates every ordinary command
   * obeys — refused after the run has ended and while an event is pending — and
   * because a session that used it is still a legal replay either way.
   */
  DebugGrantItem: 75,
  // 76 is reserved and unused. It stays reserved.

  /**
   * Jump the settlement to an Age. `a` is the Age.
   *
   * 77–82 are the handles pass's reservation, declared in COORDINATION.md
   * before any of them was taken; only this one is. It exists because the
   * silhouette work has an acceptance test — *name a building from its outline
   * at default zoom* — and that test cannot be run on a screen that can only
   * ever contain the third of the catalogue an Age-1 settlement may build.
   * `Cmd.AdvanceAge` is the game's door and stays the only one: this is the
   * harness's, numbered with the player's rather than into the 64–69 block for
   * the same reason `DebugSetDate` is, so a session that used it is still a
   * legal replay and still obeys the game-over and pending-event gates.
   */
  DebugSetAge: 77,
  // 78–82 are reserved to the same block and unused.

  /**
   * 59–63 are diplomacy. Reserved in COORDINATION.md before they were taken.
   *
   * Note what is *not* in the block: 64–69 are the debug console's and were
   * already spoken for, so the reservation of "59–70" is really 59–63 plus 70.
   * 63 and 70 are unused and stay diplomacy's — the vocabulary below is four
   * doors rather than a dozen on purpose, because a system with one command per
   * verb ends up with fifteen commands that each do one thing and no way to see
   * the shape of it. `CampaignDiplomacy` carries a `DiploAction` in `a`, which is
   * where the standing arrangements live.
   */
  /** Declare war. `a` is the target nation, `b` a `CasusBelli`. */
  CampaignDeclareWar: 59,
  /**
   * Put terms on the table. `a` is the war id, `b` a `PeaceTerm`, `c` the
   * province for a cession. Whether it is a demand or a concession is decided by
   * the war score, not by the command — see `peaceTaker`.
   */
  CampaignOfferPeace: 60,
  /** Alliances, marriages, vassalage. `a` is a `DiploAction`, `b` the nation. */
  CampaignDiplomacy: 61,
  /** Answer something on your table. `a` is the offer id, `b` 1 to accept. */
  CampaignAnswerOffer: 62,

  /**
   * The curtain — towers, and the men who stand on them.
   *
   * **87–92 are this block's**, declared in COORDINATION.md before they were
   * taken, per the rule three parallel branches learned the expensive way:
   * leaving a gap is not a strategy, because everyone leaves the same gap.
   * 89–92 are unused and stay reserved here.
   */
  /**
   * Raise a tower on the curtain. `a`, `b` are the top-left tile of its 2×2
   * footprint.
   *
   * A tower is a wall kind rather than a building, so this is a wall command
   * rather than a `Build`: it swallows the sections it stands on, it has to
   * touch the line, and it is paid for and damaged as curtain.
   */
  WallTower: 87,
  /**
   * Put a company on the wall. `a` is the company, `b` and `c` the tile it is
   * pointed at, `d` is 1 to stand it down again.
   *
   * The tile need not be curtain: anything within three tiles of a wall counts,
   * because a player pointing at their own line from a tactical camera is
   * pointing at roughly the right stone.
   */
  Garrison: 88,

  /**
   * Trade — nodes, control, and the merchants you push it with.
   *
   * **93–98 are this block's**, declared in COORDINATION.md before they were
   * taken. 96–98 are unused and stay reserved here.
   *
   * Numbered out of the campaign's 40–53 run on purpose: that run is contiguous
   * and `applyCommand` routes it as a range, and stretching the range over 54–92
   * would swallow the debug console at 64–69, the cart at 71–75 and the curtain
   * at 87–88. A second small range is cheaper and says what it means. See
   * `CAMPAIGN_TRADE_CMD_FIRST` in `campaign/commands.ts`.
   *
   * Three doors rather than one with an action id — unlike `CampaignDiplomacy`,
   * which has a dozen verbs — because there are exactly two things a merchant
   * can be told and one thing you can do to him, and an action id over three
   * cases is indirection for its own sake.
   */
  /** Post a merchant to take our cut here. `a` is the trade node. */
  CampaignTradeCollect: 93,
  /**
   * Post a merchant to push this node's outflow down one road. `a` is the node
   * he stands in, `b` the downstream node he leans toward. `b` must be directly
   * downstream of `a`; the command refuses politely if it is not.
   */
  CampaignTradeSteer: 94,
  /** Call a merchant home. `a` is the node he is standing in. */
  CampaignTradeRecall: 95,
  // 96–98 are reserved to the same block and unused.

  /**
   * Holdings — the tier ladder a province is built up.
   *
   * **105–108 are this block's**, reserved in COORDINATION.md before they were
   * taken, per the rule three parallel branches learned the expensive way:
   * leaving a gap is not a strategy, because everyone leaves the same gap. They
   * sit clear of the campaign's own 40–62 run rather than inside it because that
   * run is contiguous and full at both ends.
   *
   * There are now three of these ranges — the campaign's, trade's 93–98 above,
   * and this one — and `isCampaignCommand` in `campaign/commands.ts` is the
   * single place that knows how many. `applyCommand` asks it rather than
   * spelling the bounds out, so the fourth block costs one line in one file.
   *
   * 107 and 108 are unused and stay reserved here.
   */
  /**
   * Put masons on a holding: begin building the next tier. `a` is the province.
   *
   * One command for all nine rungs, and no argument for which rung, because
   * there is only ever one next. The materials leave the supplying city's yard
   * the moment this lands; the tier arrives months later.
   */
  CampaignDevelop: 105,
  /** Call the masons off. `a` is the province. Half of what was spent comes back. */
  CampaignCancelWorks: 106,

  /**
   * Races, leaders and the Academy.
   *
   * **109–120 are this block's**, declared in COORDINATION.md before any of them
   * was taken, per the rule three parallel branches learned the expensive way:
   * leaving a gap is not a strategy, because everyone leaves the same gap.
   * 109 and 111 are taken, 110 is reserved *and will be used*, and 112–120 are
   * reserved and unused.
   */
  /**
   * Choose the kingdom this run is about. `s` is a kingdom id.
   *
   * One command rather than two, because the owner's flow is one decision: *"a
   * choice of the map, then decide which kingdom to play, which decided the race
   * and the character"*. The race and the leader are consequences of the
   * kingdom, not sibling choices, and modelling them as two commands would let
   * the UI offer a combination the world does not contain.
   *
   * Until `mapsea`'s kingdoms exist, a bare `RaceId` in `s` is accepted and
   * resolves to that race's default house — so the selection screen can be built
   * and played against the roster before the world that seats it is generated.
   */
  PickKingdom: 109,
  /**
   * 110 `RaceAct` is reserved for the one active verb a binding needs — releasing
   * the Splitmoot's mob, taking thralls, spending the Book of Grudges. It is not
   * taken yet because the binding that would use it is not built yet, and a
   * command in the vocabulary that nothing implements is worse than a gap: it
   * reads as a promise. Declared here so nobody else takes it.
   */
  /**
   * Set the race outright. `s` is a `RaceId`, `a` is nonzero to also reset the
   * house to that race's default.
   *
   * A debug command, numbered with the player's rather than into the 64–69
   * block, for the same reason `DebugSetAge` and `DebugSetDate` are: it obeys
   * the game-over and pending-event gates and a session that used it is still a
   * legal replay. It exists because eleven races cannot be tested through a
   * kingdom-selection screen that does not exist yet, and because the balance
   * harness needs to play all of them without a world map.
   *
   * It does **not** re-fold the identity passives. Those are folded once at
   * `initRun` and folding a second set on top would leave a world carrying two
   * races' modifiers — which is exactly the kind of state a replay cannot
   * reproduce. Set the race and start a run; do not set it mid-run and expect
   * the bag to follow.
   */
  DebugSetRace: 111,
  // 112–120 are reserved to the same block and unused.

  /**
   * The realm's chronicle — the questions the strategic layer puts to a crown.
   *
   * **121–126 are this block's.** They were reserved for `access`, which then
   * released all six unused because its three verbs rode `DiploAction` behind an
   * existing door; COORDINATION.md says so in as many words and invites the next
   * branch to take them. Declared before they were taken, per the rule three
   * parallel branches learned the expensive way: leaving a gap is not a strategy,
   * because everybody leaves the same gap.
   *
   * One is taken. 122–126 are reserved to this block and unused — a chronicle
   * that grew a "postpone" or a "read the whole card" verb would want a
   * neighbour, and a command in the vocabulary that nothing implements is worse
   * than a gap because it reads as a promise.
   *
   * Routed by a **fifth** range in `isCampaignCommand` — see `campaign/commands.ts`.
   * Nothing between 112 and 120 belongs to the campaign, so widening the existing
   * holdings range to reach 121 would have silently swallowed the races block.
   */
  /**
   * Answer the realm's chronicle. `s` is the event id, `a` the choice index.
   *
   * Deliberately the same shape as `Cmd.ResolveEvent` (23), which answers the
   * settlement's: the id is a staleness guard so a click from a modal that has
   * already been dismissed cannot answer the question currently on the table, and
   * `a` indexes the **offered** list frozen at draw time rather than deck order.
   */
  CampaignAnswerEvent: 121,
  // 122–126 are reserved to the same block and unused.

  /**
   * The free market — goods on sale at a price you choose, bids the same way,
   * matched at the council, cheapest ask first, at the ask.
   *
   * **133–138 are this block's**, declared in COORDINATION.md before they were
   * taken, per the rule three parallel branches learned the expensive way:
   * leaving a gap is not a strategy, because everybody leaves the same gap.
   * Three are taken; 136–138 stay reserved here — a book that grew a "repost
   * at a new price" or a "buy it out now" verb would want a neighbour.
   *
   * Routed by a **seventh** range in `isCampaignCommand` — see
   * `campaign/commands.ts`. 127–132 are free but were not this block's to
   * take, and reaching them from 126 would have swallowed whatever block
   * claims them next.
   */
  /**
   * Put goods up for sale. `a` is the resource's index in `RESOURCE_KEYS`,
   * `b` the amount, `c` the price in **tenths of a gold per unit** — the whole
   * book is fixed-point, see `markettypes.ts`. The goods are escrowed the
   * moment this lands.
   */
  MarketPostSell: 133,
  /** Post a standing bid. Same shape as the sell; no gold moves until a fill. */
  MarketPostBuy: 134,
  /** Take an order off the book. `a` is the order id. Escrow comes home. */
  MarketCancel: 135,
  // 136–138 are reserved to the same block and unused.

  /**
   * The sea — harbours, hulls, and what they carry.
   *
   * **99–104 are this block's**, declared in COORDINATION.md before they were
   * taken, for the reason the tower block above records: three parallel
   * branches learned that leaving a gap is not a strategy, because everybody
   * leaves the same gap.
   *
   * Six doors for a system with rather more than six verbs, and the compression
   * is `CampaignFleetAction` — disembarking, paying off and putting about are
   * all "do this to that fleet", and giving each its own number would have made
   * the vocabulary bigger without making it clearer. That is the same trick
   * `CampaignDiplomacy` plays with `DiploAction`, for the same reason.
   */
  /** Raise or extend a harbour. `a` is the province. */
  CampaignBuildPort: 99,
  /**
   * Lay down hulls. `a` is the port province, `b` the number of ships.
   *
   * `c` is a fleet id to add them to, or 0 for a new fleet. Fleet ids count
   * from 1, so 0 is unambiguous and an omitted `c` means what it should. This
   * shares a command rather than taking one of the two numbers left in the
   * block because "build ships at this harbour" is one intent — the answer to
   * "into which hull-count" is a detail of it, exactly as which regiment a
   * reinforcement joins is a detail of `CampaignReinforce`.
   */
  CampaignRaiseFleet: 100,
  /** Put to sea. `a` is the fleet, `b` the coast at the far end of the lane. */
  CampaignSailFleet: 101,
  /** Load a host. `a` is the fleet, `b` the army. Both must be in the harbour. */
  CampaignEmbark: 102,
  /** `a` is the fleet, `b` a `FleetAction`: put ashore, pay off, or put about. */
  CampaignFleetAction: 103,
  /** Run down a fleet at sea. `a` is the chaser, `b` the quarry. */
  CampaignFleetIntercept: 104,

  /**
   * The field: 2v2, the loadout, and the scatter.
   *
   * **146–151 are this block's**, declared in COORDINATION.md before any of
   * them was taken, per the rule three parallel branches learned the expensive
   * way: leaving a gap is not a strategy, because everybody leaves the same gap.
   *
   * **Exactly one is taken. 147–151 are released unused** — and the reason is
   * worth recording, because two of the three things this pass built wanted a
   * command and turned out not to need one. A 2v2 is decided by `BattleConfig`
   * at `createBattle`, before there is a world for a command to be applied to;
   * and the trees and rocks are rolled from the battle's own seed, so there is
   * nothing for a player to say about them. Only the loadout is a *decision*,
   * and a decision is what a command is for.
   */
  /**
   * Edit the army you are taking onto the field. `a` is a `LoadoutAct` — add,
   * remove, clear, or spend the rest of the purse — and `b` is a unit type index.
   *
   * One door with an action id rather than four numbers, which is the trick
   * `CampaignDiplomacy` and `CampaignFleetAction` both play: "change my order of
   * battle" is one intent, and four commands for it would be four things the
   * UI, the harness and any future bot each had to learn separately.
   *
   * Deployment only. Once the horns have gone, the army on the field is the army
   * you have — see `applyLoadout`.
   */
  BattleLoadout: 146,
  // 147–151 are reserved to the same block and unused.

  /**
   * Standing garrisons, and hiding a host inside the walls.
   *
   * **164–169 are this block's**, declared in the reservation table at the end
   * of `COORDINATION.md` before any of them was taken, per the rule three
   * parallel branches learned the expensive way: leaving a gap is not a
   * strategy, because everybody leaves the same gap.
   *
   * **Three are taken. 167–169 are reserved to the block and unused.** Routed
   * by an **eighth** range in `isCampaignCommand` — see `campaign/commands.ts`
   * for why this is a new range and not a widening of an old one. 152–163
   * between the field's block and this one belong to other branches of the same
   * batch, and reaching them from 151 would swallow every one of them.
   *
   * Two doors for shelter and not one toggle, deliberately. A toggle is only
   * safe while the click and the tick agree about which state the host is in,
   * and this whole file's convention is that a command that has become
   * meaningless between the two is an ordinary event: `CampaignShelterArmy` on
   * a host already inside is a no-op, and so is the sally on one already out.
   * A toggle would instead do the opposite of what was asked.
   */
  /**
   * Put a host inside the walls it is standing in. `a` is the army.
   *
   * Refused unless the ground is its own crown's, the holding has walls or a
   * name, and there is room behind the gate — `shelterRefusal` in
   * `campaign/garrisons.ts` is the whole clause list, and the world map greys
   * the button off the same one.
   */
  CampaignShelterArmy: 164,
  /** Bring them back out into the field. `a` is the army. Always legal. */
  CampaignSallyArmy: 165,
  /**
   * What the player's own city amounts to, in the two terms the strategic map
   * did not have. `a` is the province, `b` the points of standing garrison its
   * military works are worth, `c` the Age the settlement stands in.
   *
   * A command rather than a write for exactly the reason `SetCapitalStanding`
   * is one: walking out to the map is a *local* act — one player does it, the
   * other may be laying bricks — but the measure it hands the realm is shared
   * state, and written directly it desynchronises the room. It rides beside
   * `SetCapitalStanding` rather than inside it because the Age is not a
   * property of a province and the two numbers `SetCapitalStanding` carries are
   * already all of it.
   */
  CampaignSetWatch: 166,
  // 167–169 are reserved to the same block and unused.

  /**
   * One order, several hosts.
   *
   * **208–213 are this block's** — the campaign board's remaining controls —
   * declared in the reservation table at the end of `COORDINATION.md` before
   * any of them was taken. **One is taken. 209–213 are released by name**: the
   * levy screen turned out to need no door of its own (it raises hosts through
   * `CampaignRaiseArmy`, which has existed since the realm did) and the
   * two-hosts-in-one-province gesture is a selection, which is a fact about a
   * screen and not about a realm.
   *
   * `a` is the destination province. `s` is the hosts, their ids joined by
   * commas — the string and not `a`…`d`, because a crown may field five hosts
   * and the four number slots cannot carry five ids and a destination.
   *
   * **Why this is one command and not N `CampaignMoveArmy`s.** The UI could
   * perfectly well push four of those and the board would look identical. Two
   * things say otherwise. The first is atomicity: `applyCampaignCommand`
   * refuses everything the moment `c.pending` is set, and a battle that lands
   * between the second and third order of one gesture would take half of it
   * with no way for the player to tell which half. The second is the log — a
   * group order is *one* thing the player did, and four lines saying so is four
   * lines of chronicle for one click.
   *
   * What it does **not** do is make the hosts a formation. Each one is ordered
   * to the destination on its own and walks its own route at its own pace;
   * nothing waits, nothing merges, and a host that cannot go is skipped rather
   * than refusing for the rest. That is the reference genre's answer and not
   * the tactical one — armies on this map are counters with orders, not a line
   * with a facing, and `CampaignMergeArmies` is still the only thing that makes
   * two hosts into one.
   */
  CampaignMarchGroup: 208,
  // 209–213 are released by name; see above.

  /**
   * Resource nodes, depletion, hauling, and putting a town back together.
   *
   * **140–145 are this block's**, declared in COORDINATION.md before any of them
   * was taken, per the rule three parallel branches learned the expensive way:
   * leaving a gap is not a strategy, because everybody leaves the same gap.
   *
   * **Two are taken. 142–145 are released by name** — they are free for the
   * next branch, and this is worth saying plainly rather than leaving them
   * "reserved and unused", because the two things they were held for turned out
   * not to need a door of their own:
   *
   *   *rebuilding from rubble* already had one. `Cmd.RebuildRubble` (17) has
   *   existed since the settlement layer and does exactly what the brief asks —
   *   it puts back what the rubble remembers, at its remembered footprint, and
   *   charges the full price. What it lacked was a way to *ask it* whether it
   *   would work; that is `rebuildRefusal`, a reader, not a command.
   *
   *   *ordering a haul* is not a decision. A worker hauls because the camp that
   *   employs him stands where it stands; the player's verb for "get the timber
   *   from over there instead" is, and should stay, moving the camp.
   */
  /**
   * Put a crew on this building now. `a` is the building index, `b` is 1 to
   * cancel the order.
   *
   * The only thing in the game that mends a building **during a Siege**, which
   * is why it is a command rather than a checkbox: the automatic gang is
   * switched off while the horns are up, on purpose, and this is the player
   * deciding that one particular wall is worth the masons standing in the open
   * for. It also jumps the queue and doubles the crew between waves. The
   * materials are the ordinary `REPAIR_COST_FRACTION`, so it is a decision and
   * not a free button.
   */
  /**
   * Lay a bridge across deep water. A dragged line, paid per tile, on the
   * `MoatLine` (25) model.
   *
   * Only over `Terrain.Deep` — a bridge on dry ground is a road, and a bridge
   * over a ford is a waste of timber the ford does not need. That refusal is
   * what makes the tool teach the map: drag it anywhere else and nothing
   * happens, drag it over the channel and the channel stops being a problem.
   */
  BridgeLine: 142,
  OrderRepair: 140,
  /**
   * Take the wood down. `a`, `b` are a tile, `c` a radius in tiles, `s` a node
   * kind id (`tree` / `rock` / `ore`) or empty for every kind.
   *
   * A debug command, numbered with the player's rather than into the 64–69
   * block, for the same reason `DebugSetAge` and `DebugSetDate` are: it obeys
   * the game-over and pending-event gates and a session that used it is still a
   * legal replay. It exists because depletion is otherwise a forty-minute
   * proposition to observe, and because the rule of this codebase is that the
   * simulation is written to through one door — a test that reached into
   * `w.nodes` to empty a patch would be testing a world no player can reach.
   */
  DebugClearNodes: 141,
  // 142–145 are this block's and are released unused. Take them.

  /**
   * The AI's towns, and the wall a besieger could not get through.
   *
   * **170–175 are this block's**, declared in COORDINATION.md before any of
   * them was taken, per the rule three parallel branches learned the expensive
   * way: leaving a gap is not a strategy, because everybody leaves the same
   * gap.
   *
   * **One is taken. 171–175 are released by name**, and it is worth saying
   * which doors were considered and refused, because most of this pass turned
   * out not to want one:
   *
   *   *whose walls these are* is `World.wallTeam`, and it is decided at
   *   `createBattle` — before there is a world for a command to be applied to,
   *   exactly as a 2v2 is. A command to change it mid-battle would be a command
   *   to change sides.
   *
   *   *the way round a wall* is `Company.route`, and it is not a decision
   *   either: it is laid by `orderCompany` as part of obeying the move order
   *   the player already gave. A second command for "and go round" would be a
   *   button for something a company should never have needed telling.
   *
   *   *raising the town* on a campaign field is `BattleConfig.town`, read off
   *   the province. The one thing left that a person genuinely wants to *say*
   *   is "put one here so I can look at it", which is what this is.
   */
  /**
   * Raise an AI town centred on a tile. `a`, `b` are the tile; `c` is the
   * holding's rung, 1–10; `d` is the fortification level, 0–3.
   *
   * A debug command, numbered into the player's block rather than the 64–69
   * one for the reason `DebugClearNodes` gives: it obeys the game-over and
   * pending-event gates, so a session that used it is still a legal replay, and
   * the town it raises is a pure function of the world's own seeded stream.
   *
   * It exists because the alternative way to look at a generated town is to
   * play a campaign until somebody fortifies a province and then march on it,
   * and because the rule of this codebase is that the simulation is written to
   * through one door — a test that called `raiseTown` directly would be testing
   * a world no player can reach.
   */
  RaiseTown: 170,
  // 171–175 are this block's and are released unused. Take them.

  /**
   * ── Owning more than one town: 200–207, and two of them are taken ─────────
   *
   * *"you can swap between towns once you capture to build both they should be
   * seperate, you need to rebuild the other town after battle"*.
   *
   * Only two verbs turned out to be decisions, and that is worth stating
   * because the item is the largest on the backlog and looked like it would
   * want six. **Swapping between towns is not a command** — it is the Tab key,
   * a view change, exactly as walking between the realm and the capital has
   * always been, and giving it a command would put "which screen is this player
   * looking at" into a shared, hashed, replayed world. **Founding is not a
   * separate verb from entering**, because the first entry *is* the founding.
   * And **rebuilding already has its commands**: `RebuildRubble` (17) and
   * `ClearRubble` (18), which is the whole reason a wrecked town is expressed
   * as rubble rather than as a new kind of ruin.
   *
   * `202–207` are released by name, and unused.
   */
  /**
   * Stand in a holding of yours as a place. `a` is the province index.
   *
   * Applied to the **realm**. It records that this ground now has a settlement
   * world of the player's on it (`ProvinceState.settled`) and clears the sack
   * that arrival has just paid for (`ProvinceState.wreck`). Both are campaign
   * state — they decide whether this province's economy runs, whether its own
   * development is written back when the player leaves, and what the next
   * arrival owes — so both go through this door rather than being written by
   * the code that opens the screen.
   *
   * The *world* is not created here, and deliberately not: `applyCommand`
   * mutates the world it is given, and a command that conjured a second one
   * would be the one place in this project where that is not true.
   * `createHoldWorld` is a pure function of the realm and the province, in the
   * same relationship to this command that `createBattle` is to the pending
   * record — see `campaign/holdworld.ts`.
   */
  EnterHold: 200,
  /**
   * Bring a fraction of this settlement down. `a` is that fraction in permille.
   *
   * Applied to the **town**, not to the realm: it is the town that is being
   * broken. A holding taken by storm arrives wrecked, and the wreck is paid on
   * arrival because at the moment of capture there may be no settlement world
   * here at all — see `ProvinceState.wreck`.
   *
   * A command rather than a call, because it is a large, seeded, irreversible
   * mutation of a saved world. Written directly it would be the one change in
   * the game that a replay of the command log could not reproduce, and the
   * failure would look like "the second town is different every time you load
   * it". See `systems/sack.ts`.
   */
  WreckTown: 201,
  // 202–207 are this block's and are released unused. Take them.

  /**
   * The guided run: five waves with their parameters bent to teach five verbs.
   *
   * **218–223 are this block's**, declared in COORDINATION.md before either was
   * taken. **Two are taken; 220–223 are released by name**, and it is worth
   * saying which doors were considered and refused, because a tutorial is
   * exactly the thing that tempts you into inventing a vocabulary for itself:
   *
   *   *advancing a lesson* is not a decision anybody makes. The step moves when
   *   a wave ends, in `endSiege`, because the wave **is** the lesson's exam —
   *   see `systems/tutorial.ts`. A command for it would be the card telling the
   *   simulation what happened, which is backwards, and it would be a way for a
   *   client to skip an exam it had failed.
   *
   *   *placing the first woodcutter for you* was the other temptation, and it
   *   is the thing this whole pass exists not to do. Every lesson is completed
   *   through the ordinary door — `Build`, `WallLine`, `Recruit`, `MoveCompany`,
   *   `CastPower` — or it is not completed, and the run carries on regardless.
   */
  /**
   * Begin the lessons. No argument.
   *
   * Refused anywhere it would be a cheat rather than a lesson: a guided wave is
   * a *weaker* wave, so `beginGuidedRun` takes it only on a standalone
   * settlement that has not yet seen its first horn.
   */
  TutorialBegin: 218,
  /**
   * Leave the lessons, at any moment, from any step. Idempotent.
   *
   * The director stops bending on the very next plan and the run carries on at
   * whatever wave it has reached — which is the whole reason the guided run is
   * a bend on an ordinary world rather than a mode: there is nothing to unwind.
   */
  TutorialSkip: 219,
  // 220–223 are this block's and are released unused. Take them.

  // Debug console. Present in shipped builds behind the backtick key; the bots
  // never emit these, so a bot run is always a legal run.
  DebugSpawn: 64,
  DebugGive: 65,
  DebugSetWave: 66,
  DebugKillAll: 67,
  DebugGod: 68,
  /** Force a Chronicle event. `s` is the event id, or empty for the next eligible one. */
  DebugEvent: 69,
} as const;
export type CmdKind = (typeof Cmd)[keyof typeof Cmd];

/**
 * The debug console's own block, which is exempt from the two gates every other
 * command obeys — it still works on the death screen and behind a Chronicle
 * modal, because a tester needs it most exactly then.
 *
 * This used to be written as `k < Cmd.DebugSpawn`, i.e. "anything numbered 64 or
 * above is debug", which was true for exactly as long as 64 was the top of the
 * vocabulary. The moment a player-facing block opened above it — the cart, at
 * 71 — equipping a sword became something you could do after losing the run and
 * while the world was frozen waiting on an answer. A range that says what it
 * means cannot rot the same way.
 */
export const DEBUG_CMD_FIRST = 64;
export const DEBUG_CMD_LAST = 69;

export function isDebugCommand(k: number): boolean {
  return k >= DEBUG_CMD_FIRST && k <= DEBUG_CMD_LAST;
}

export interface Command {
  k: number;
  a: number;
  b: number;
  c: number;
  d: number;
  s: string;
  /**
   * The crown that gave the order, where that is not obvious.
   *
   * Unset everywhere the answer can only be one thing: the UI of a machine
   * playing one kingdom, the bots, every replay in the harness. It is set on
   * the orders that arrive over a wire, because a room is several crowns and
   * one realm — see `applyCampaignCommand`, which is the only reader.
   *
   * Optional rather than defaulted, and left out of `cmd()` on purpose: a
   * command that names a nation it did not come from is a forged order, so the
   * field is written in exactly one place (the server, from the seat the
   * message arrived on) and nowhere a caller could get it wrong.
   */
  n?: number;
}

export function cmd(k: number, a = 0, b = 0, c = 0, d = 0, s = ''): Command {
  return { k, a, b, c, d, s };
}

/** A recorded run: everything needed to replay it byte-for-byte. */
export interface CommandLog {
  seed: number;
  factionId: string;
  difficultyId: string;
  /** Commands keyed by the tick they are applied on. */
  entries: Array<{ tick: number; cmds: Command[] }>;
}

export function newLog(seed: number, factionId: string, difficultyId: string): CommandLog {
  return { seed, factionId, difficultyId, entries: [] };
}

export function record(log: CommandLog, tick: number, cmds: Command[]): void {
  if (cmds.length === 0) return;
  log.entries.push({ tick, cmds: cmds.map((c) => ({ ...c })) });
}

export class CommandQueue {
  private pending: Command[] = [];

  push(c: Command): void {
    this.pending.push(c);
  }

  pushAll(cs: readonly Command[]): void {
    for (const c of cs) this.pending.push(c);
  }

  /** Take everything queued; the caller owns the returned array. */
  drain(): Command[] {
    if (this.pending.length === 0) return EMPTY;
    const out = this.pending;
    this.pending = [];
    return out;
  }

  get length(): number {
    return this.pending.length;
  }
}

const EMPTY: Command[] = [];
